/**
 * Status de revisão de minuta do Escaninho de Pauta.
 *
 * O backend trabalha com o campo `numero`. Mantemos `codigo` como alias para
 * compatibilidade com os componentes já existentes.
 */
export interface IStatusRevisao {
    numero: number;
    codigo: number;
    codigoMagistrado?: string;
    descricao: string;
    cor: string;
}

const CORES_POR_DESCRICAO: Record<string, string> = {
    'em revisão': '#f9a825',
    'devolvido para correção': '#b71c1c',
    'minuta refeita': '#1565c0',
    'revisado': '#2e7d32'
};

function normalizarTexto(valor?: string): string {
    return (valor || '')
        .trim()
        .toLowerCase();
}

/** Cor dos status padrão, usada também para migrar registros criados antes da coluna `cor`. */
export function corPadraoStatusRevisao(descricao?: string): string | undefined {
    return CORES_POR_DESCRICAO[normalizarTexto(descricao)];
}

export function corTextoContraste(cor?: string): '#000' | '#fff' {
    const hexadecimal = cor?.trim();
    if (!/^#[0-9a-f]{6}$/i.test(hexadecimal || '')) {
        return '#000';
    }

    const canais = [1, 3, 5].map(inicio => parseInt(hexadecimal!.slice(inicio, inicio + 2), 16) / 255);
    const [vermelho, verde, azul] = canais.map(canal =>
        canal <= 0.03928 ? canal / 12.92 : Math.pow((canal + 0.055) / 1.055, 2.4)
    );
    const luminancia = 0.2126 * vermelho + 0.7152 * verde + 0.0722 * azul;
    const maiorCanal = Math.max(...canais);
    const saturacao = maiorCanal === 0 ? 0 : (maiorCanal - Math.min(...canais)) / maiorCanal;
    const contrasteBranco = 1.05 / (luminancia + 0.05);

    // Em cores vivas de luminosidade média, o branco reduz a sensação de brilho
    // sem prejudicar demais a leitura. Em cores claras (amarelo/ciano), mantém preto.
    if (saturacao >= 0.65 && contrasteBranco >= 3) {
        return '#fff';
    }

    return luminancia > 0.179 ? '#000' : '#fff';
}

export function normalizarStatusRevisao(
    status: { numero?: number; codigo?: number; codigoMagistrado?: string; descricao?: string; cor?: string },
): IStatusRevisao | undefined {
    const numero = status?.numero ?? status?.codigo;
    if (numero === undefined || numero === null) {
        return undefined;
    }

    const descricao = status?.descricao || '';
    const corDoBackend = status?.cor?.trim();
    const corPorDescricao = corPadraoStatusRevisao(descricao);
    const cor = /^#[0-9a-f]{6}$/i.test(corDoBackend || '')
        ? corDoBackend!
        : corPorDescricao || '#000';

    return {
        numero,
        codigo: numero,
        codigoMagistrado: status?.codigoMagistrado,
        descricao,
        cor,
    };
}

export function statusRevisaoPorCodigo(codigo?: number, lista: IStatusRevisao[] = []): IStatusRevisao | undefined {
    if (codigo === undefined || codigo === null) {
        return undefined;
    }
    return lista.find(s => s.codigo === codigo || s.numero === codigo);
}
